import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { RuleArticle, Sport } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { Icon } from '../../components/shared/Icons';
import { Skeleton } from '../../components/shared/Skeleton';
import { Button } from '../../components/shared/Button';
import { Modal } from '../../components/shared/Modal';
import { Spinner } from '../../components/shared/Spinner';
import { apiErrorMessage } from '../../lib/api';
import { notifySuccess, notifyError } from '../../lib/toast';
import { useSportsQuery, useRulesQuery } from '../../hooks/use-sports';
import { useDeleteRuleArticle } from '../../hooks/use-rules-admin';
import { usePagination } from '../../hooks/use-pagination';
import { DataPagination } from '../../components/shared/DataPagination';
import { RuleArticleFormModal } from '../../components/admin/RuleArticleFormModal';
import { AIWizardDialog } from '../../components/admin/AIWizardDialog';

// Rule articles per page (the pager hides itself at or below this count). RuleBook is keyed by
// sport slug, so switching sport remounts it and pagination resets to page 1.
const RULES_PAGE_SIZE = 8;

// One rule article: number + title, summary, the at-a-glance key points, and (super_admin)
// edit/delete affordances that reveal on hover.
function RuleArticleCard({
  article,
  onEdit,
  onDelete,
}: {
  article: RuleArticle;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <article className="group relative rounded border border-line bg-surface p-4 shadow-1">
      {/* Row actions — subtle until hover so the reading view stays clean. */}
      <div className="absolute right-3 top-3 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('admin.rulebooks.editRuleAria', { number: article.article_number })}
          className="grid h-7 w-7 place-items-center rounded-sm text-ink-2 hover:bg-surface-2 hover:text-ink"
        >
          <Icon.pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label={t('admin.rulebooks.deleteRuleAria', { number: article.article_number })}
          className="grid h-7 w-7 place-items-center rounded-sm text-ink-2 hover:bg-danger-tint hover:text-danger"
        >
          <Icon.trash className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex items-baseline gap-2.5 pr-16">
        <span className="grid h-6 min-w-6 place-items-center rounded-sm bg-accent-tint px-1.5 font-mono text-[12px] font-bold text-accent">
          {article.article_number}
        </span>
        <h3 className="text-[14px] font-semibold text-ink">{article.title}</h3>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-2">{article.summary}</p>
      {article.key_points.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1.5">
          {article.key_points.map((point, i) => (
            <li key={i} className="flex gap-2 text-[12.5px] leading-relaxed text-muted">
              <Icon.check className="mt-0.5 w-3.5 h-3.5 flex-none text-accent" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// Delete-confirm dialog for a rule article.
function DeleteRuleDialog({
  slug,
  article,
  onClose,
}: {
  slug: string;
  article: RuleArticle;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const del = useDeleteRuleArticle(slug);
  async function confirm() {
    try {
      await del.mutateAsync(article.id);
      notifySuccess(t('admin.rulebooks.deletedToast'));
      onClose();
    } catch (err) {
      notifyError(err, t('admin.rulebooks.deleteErrorToast'));
    }
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin.rulebooks.deleteDialogTitle', { number: article.article_number })}
      panelClass="max-w-md"
      footer={
        <>
          <Button variant="default" size="md" onClick={onClose} disabled={del.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" size="md" onClick={confirm} disabled={del.isPending}>
            {del.isPending ? <Spinner className="h-4 w-4" /> : t('admin.rulebooks.deleteButton')}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2">
        <Trans
          i18nKey="admin.rulebooks.deleteBody"
          values={{ title: article.title }}
          components={{ bold: <span className="font-semibold text-ink" /> }}
        />
      </p>
    </Modal>
  );
}

// The selected sport's rule-book, with authoring: add/edit/delete + the AI wizard.
function RuleBook({ sport }: { sport: Sport }) {
  const { t } = useTranslation();
  const slug = sport.slug;
  const { data, isLoading, isError, error } = useRulesQuery(slug);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<RuleArticle | null>(null);
  const [deleting, setDeleting] = useState<RuleArticle | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const articles = data ?? [];
  // Paginate the fetched rule-book client-side; the pager slices `articles` into pages.
  const { page, setPage, pageCount, pageItems } = usePagination(articles, RULES_PAGE_SIZE);

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar — always available so the admin can author into an empty book too. */}
      <div className="flex items-center justify-between">
        <p className="text-[12px] font-medium uppercase tracking-[0.05em] text-muted">
          {data ? t('admin.rulebooks.articleCount', { count: articles.length }) : t('admin.rulebooks.ruleBookFallback')}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="default" size="sm" onClick={() => setAdding(true)}>
            <Icon.plus className="h-3.5 w-3.5" />
            {t('admin.rulebooks.addRule')}
          </Button>
          <Button variant="accent" size="sm" onClick={() => setWizardOpen(true)}>
            <Icon.spark className="h-3.5 w-3.5" />
            {t('admin.rulebooks.aiWizard')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </>
      ) : isError && !data ? (
        <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {apiErrorMessage(error, t('admin.rulebooks.couldNotLoadRulebook'))}
        </div>
      ) : articles.length === 0 ? (
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-16 text-center">
          <p className="text-sm text-muted">
            {t('admin.rulebooks.noArticlesYet')}
          </p>
        </div>
      ) : (
        <>
          {pageItems.map((article) => (
            <RuleArticleCard
              key={article.id}
              article={article}
              onEdit={() => setEditing(article)}
              onDelete={() => setDeleting(article)}
            />
          ))}
          <DataPagination
            className="mt-1"
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
          />
        </>
      )}

      {adding && <RuleArticleFormModal slug={slug} onClose={() => setAdding(false)} />}
      {editing && (
        <RuleArticleFormModal slug={slug} existing={editing} onClose={() => setEditing(null)} />
      )}
      {deleting && (
        <DeleteRuleDialog slug={slug} article={deleting} onClose={() => setDeleting(null)} />
      )}
      {wizardOpen && (
        <AIWizardDialog slug={slug} sportName={sport.name} onClose={() => setWizardOpen(false)} />
      )}
    </div>
  );
}

export function RulebooksPage() {
  const { t } = useTranslation();
  const { data: sports, isLoading, isError, error } = useSportsQuery();
  // Which sport's rule-book is shown. Defaults to the first sport once loaded.
  const [activeSlug, setActiveSlug] = useState<string | undefined>();

  // Select the first sport as soon as the catalogue arrives (and only while nothing is
  // selected yet — don't override the user's choice on a background refetch).
  useEffect(() => {
    if (!activeSlug && sports && sports.length > 0) {
      setActiveSlug(sports[0]?.slug);
    }
  }, [sports, activeSlug]);

  const activeSport = sports?.find((s) => s.slug === activeSlug);

  return (
    <>
      <PageHeader
        title={t('admin.rulebooks.title')}
        sub={t('admin.rulebooks.sub')}
      />

      {isLoading ? (
        <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
          {/* First-load placeholder mirroring the selector + rule-book split. */}
          <div className="flex flex-col gap-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        </div>
      ) : isError && !sports ? (
        <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {apiErrorMessage(error, t('admin.sports.couldNotLoad'))}
        </div>
      ) : !sports || sports.length === 0 ? (
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-16 text-center">
          <p className="text-sm text-muted">{t('admin.rulebooks.noSportsYet')}</p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
          {/* Sport selector */}
          <nav className="flex flex-col gap-1">
            {sports.map((sport) => {
              const isActive = sport.slug === activeSlug;
              return (
                <button
                  key={sport.id}
                  type="button"
                  onClick={() => setActiveSlug(sport.slug)}
                  className={[
                    'flex items-center gap-2.5 rounded-sm border px-3 py-2.5 text-left text-[13px] font-medium',
                    isActive
                      ? 'border-line bg-surface text-ink shadow-1'
                      : 'border-transparent text-ink-2 hover:bg-surface-2 hover:text-ink',
                  ].join(' ')}
                >
                  <Icon.whistle className={'w-4 h-4 flex-none ' + (isActive ? 'text-accent' : 'text-ink-3')} />
                  <span className="min-w-0 truncate">{sport.name}</span>
                </button>
              );
            })}
          </nav>

          {/* Selected rule-book */}
          {activeSport ? <RuleBook key={activeSport.slug} sport={activeSport} /> : null}
        </div>
      )}
    </>
  );
}
